import { ScenarioRunState } from '../api/api';

/**
 * How a live stream ended.
 *
 * - `ended`: the API closed it, which it does when the run is over.
 * - `unavailable`: the API does not carry this run, so its state document is all there is.
 * - `dropped`: the connection went away halfway; the page falls back to reading the state document.
 */
export type LiveStreamOutcome = 'ended' | 'unavailable' | 'dropped';

/**
 * Follows a run the dashboard is carrying, one state per change.
 *
 * The generated client cannot read a stream, so this is plain fetch. It sends the same Authorization header as
 * the axios instance, which is what the API checks the role against.
 */
export const streamScenarioRun = async (
    url: string,
    authorization: string | undefined,
    onState: (state: ScenarioRunState) => void,
    signal: AbortSignal
): Promise<LiveStreamOutcome> => {
    const headers: Record<string, string> = { Accept: 'text/event-stream' };

    if (authorization) {
        headers['Authorization'] = authorization;
    }

    let response: Response;

    try {
        response = await fetch(url, { headers, signal });
    } catch {
        return signal.aborted ? 'ended' : 'dropped';
    }

    if (!response.ok || !response.body) {
        return 'unavailable';
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
        while (true) {
            const { done, value } = await reader.read();

            if (done) {
                return 'ended';
            }

            buffer += decoder.decode(value, { stream: true });

            // Server-sent events are separated by a blank line; a comment line (": keep-alive") carries no data
            // and only keeps the proxies in front of the API from closing a quiet connection.
            let boundary = buffer.indexOf('\n\n');

            while (boundary >= 0) {
                const block = buffer.slice(0, boundary);

                buffer = buffer.slice(boundary + 2);

                const data = block
                    .split('\n')
                    .filter((line) => line.startsWith('data:'))
                    .map((line) => line.slice('data:'.length).trimStart())
                    .join('\n');

                if (data) {
                    onState(JSON.parse(data) as ScenarioRunState);
                }

                boundary = buffer.indexOf('\n\n');
            }
        }
    } catch {
        return signal.aborted ? 'ended' : 'dropped';
    }
};
